import { Sunrise, Sun, Moon, PhoneCall, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const RomiiBubble = ({ children }) => (
  <div className="flex items-start gap-3">
    <img src="/assets/romii-icon.png" alt="" className="h-7 w-7 mt-0.5 shrink-0" />
    <p className="rounded-2xl rounded-tl-md bg-[#7F9F43]/[0.08] border border-[#7F9F43]/15 px-5 py-4 text-sm md:text-base text-[#3d4a1e] leading-relaxed font-serif-italic">
      {children}
    </p>
  </div>
);

const SarahBubble = ({ children }) => (
  <p className="ml-auto max-w-[85%] rounded-2xl rounded-tr-md bg-white border border-gray-100 px-5 py-3.5 text-sm md:text-base text-gray-600 leading-relaxed card-soft">
    {children}
  </p>
);

const steps = [
  {
    icon: Sunrise,
    time: "Morning",
    step: "Step 1",
    img: "/assets/day-morning.jpg",
    alt: "Sarah waking up in a sunlit bedroom",
    content: (
      <RomiiBubble>
        "Good morning, Sarah. Today is Tuesday. You have breakfast at 8:30. Your daughter Emma
        will visit this afternoon."
      </RomiiBubble>
    ),
  },
  {
    icon: Sun,
    time: "Afternoon",
    step: "Step 2",
    img: "/assets/day-afternoon.jpg",
    alt: "Sarah looking for her glasses near her favorite chair",
    intro: "Sarah forgets where she kept her glasses.",
    content: (
      <>
        <SarahBubble>"Romii, where are my glasses?"</SarahBubble>
        <RomiiBubble>
          "Yesterday you told me you placed them beside your favorite chair."
        </RomiiBubble>
      </>
    ),
  },
  {
    icon: Moon,
    time: "Evening",
    step: "Step 3",
    img: "/assets/day-evening.jpg",
    alt: "Sarah outside at dusk while Emma approaches",
    intro: "Sarah feels confused while outside. Romii notices unusual behaviour.",
    content: (
      <>
        <RomiiBubble>"Would you like me to call Emma?"</RomiiBubble>
        <div className="flex items-center gap-3 text-sm text-gray-500 pl-10">
          <span className="h-8 w-8 rounded-full bg-[#E89A4D]/10 flex items-center justify-center text-[#E89A4D]">
            <PhoneCall size={14} />
          </span>
          Emma answers.
        </div>
        <div className="flex items-center gap-3 text-sm font-medium text-[#111827] pl-10">
          <span className="h-8 w-8 rounded-full bg-[#7F9F43]/10 flex items-center justify-center text-[#7F9F43]">
            <Check size={14} />
          </span>
          Everything is okay.
        </div>
      </>
    ),
  },
];

export default function DayWithRomii() {
  return (
    <section className="py-24 lg:py-32" data-testid="day-with-romii-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow number="04" label="How It Works" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#111827] max-w-2xl">
            A Day with <span className="font-serif-italic text-[#7F9F43]">Romii.</span>
          </h2>
        </Reveal>

        <div className="mt-16 lg:mt-24 space-y-16 lg:space-y-24">
          {steps.map((s, i) => (
            <Reveal key={s.time} delay={0.05}>
              <div
                className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                  i % 2 === 1 ? "" : ""
                }`}
                data-testid={`day-step-${s.time.toLowerCase()}`}
              >
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2 lg:col-start-8" : ""}`}>
                  <div className="rounded-3xl overflow-hidden card-soft">
                    <img
                      src={s.img}
                      alt={s.alt}
                      className="w-full aspect-[3/2] object-cover hover:scale-[1.03] transition-transform duration-700"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1 lg:col-start-1" : "lg:col-start-7"}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <span className="h-10 w-10 rounded-xl bg-[#E89A4D]/10 flex items-center justify-center text-[#E89A4D]">
                      <s.icon size={19} strokeWidth={1.75} />
                    </span>
                    <div>
                      <p className="text-[11px] tracking-[0.25em] uppercase text-gray-400 font-semibold">{s.step}</p>
                      <p className="text-lg font-semibold text-[#111827]">{s.time}</p>
                    </div>
                  </div>
                  {s.intro && <p className="mb-5 text-gray-500 text-base md:text-lg">{s.intro}</p>}
                  <div className="space-y-4">{s.content}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
